import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import BarChartIcon from '@material-ui/icons/BarChart';
import ExtensionIcon from '@material-ui/icons/Extension';
import { DashboardMockProvider } from '../../../../../context/DashboardContext';
import {
    IndividualScoreOverviewSection,
    IndividualCompetenciesSection,
    IndividualContextSupportSection,
    IndividualLearningProgressSection,
} from '../../../../IndividualDashboard';
import { buildSampleIndividualDashboard } from '../../../../IndividualDashboard/sampleDashboardData';

const ORANGE = '#FF6900';
const GREY = '#A5A5A5';

/* The live Individual Dashboard's menu (IndividualDashboardList), switched in
   local state instead of by route — this page lives inside the program preview. */
const NAV = [
    {
        label: 'Baseline Results',
        icon: BarChartIcon,
        children: [
            { key: 'overview',  label: 'Overview',          Section: IndividualScoreOverviewSection },
            { key: 'readiness', label: 'Readiness',         Section: IndividualCompetenciesSection },
            { key: 'context',   label: 'Context & Support', Section: IndividualContextSupportSection },
        ],
    },
    { key: 'learning', label: 'Learning Progress', icon: ExtensionIcon, Section: IndividualLearningProgressSection },
];
const SECTIONS = NAV.flatMap((n) => n.children || [n]);

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        gap: 24,
        paddingTop: 18,
        paddingBottom: 24,
        [theme.breakpoints.down('sm')]: { flexDirection: 'column', gap: 12 },
    },
    nav: {
        width: 230,
        flexShrink: 0,
        alignSelf: 'flex-start',
        position: 'sticky',
        top: 0,
        borderRadius: 12,
        border: '1px solid var(--brand-border, #E5E7EB)',
        backgroundColor: 'var(--brand-surface, #fff)',
        padding: '10px 0',
        [theme.breakpoints.down('sm')]: {
            position: 'static',
            width: '100%',
            display: 'flex',
            overflowX: 'auto',
            padding: 6,
            gap: 4,
        },
    },
    group: {
        [theme.breakpoints.down('sm')]: { display: 'contents' },
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: 'calc(100% - 20px)',
        margin: '2px 10px',
        padding: '10px 12px',
        border: 'none',
        borderRadius: 10,
        background: 'none',
        color: GREY,
        fontSize: 15,
        fontWeight: 600,
        fontFamily: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'rgba(237,102,40,0.08)' },
        [theme.breakpoints.down('sm')]: {
            width: 'auto',
            margin: 0,
            whiteSpace: 'nowrap',
            fontSize: 14,
        },
    },
    groupHeader: {
        color: ORANGE,
        cursor: 'default',
        '&:hover': { backgroundColor: 'transparent' },
        [theme.breakpoints.down('sm')]: { display: 'none' },
    },
    child: {
        paddingLeft: 24,
        fontSize: 14,
        [theme.breakpoints.down('sm')]: { paddingLeft: 12 },
    },
    active: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': { backgroundColor: ORANGE },
    },
    content: {
        flex: 1,
        minWidth: 0,
    },
}));

/**
 * "View Impact" tab of the program-creation preview: the real Individual
 * Dashboard pages, fed a sample payload instead of the API, with their menu
 * as local tabs. Every chart, sort toggle and table control works as live.
 */
const IndividualDashboardPreview = ({ programName }) => {
    const classes = useStyles();
    const [activeKey, setActiveKey] = useState(SECTIONS[0].key);
    const data = useMemo(() => buildSampleIndividualDashboard({ programName }), [programName]);
    const { Section } = SECTIONS.find((s) => s.key === activeKey);

    const renderItem = ({ key, label, icon: Icon }, isChild) => (
        <button
            key={key}
            type="button"
            className={`${classes.item} ${isChild ? classes.child : ''} ${activeKey === key ? classes.active : ''}`}
            onClick={() => setActiveKey(key)}
        >
            {Icon && <Icon fontSize="small"/>}
            {label}
        </button>
    );

    return (
        <div className={classes.root}>
            <nav className={classes.nav} aria-label="Individual dashboard sections">
                {NAV.map((n) => (n.children ? (
                    <div key={n.label} className={classes.group}>
                        <div className={`${classes.item} ${classes.groupHeader}`}>
                            <n.icon fontSize="small"/>
                            {n.label}
                        </div>
                        {n.children.map((c) => renderItem(c, true))}
                    </div>
                ) : renderItem(n, false)))}
            </nav>
            <div className={classes.content}>
                <DashboardMockProvider data={data}>
                    <Section/>
                </DashboardMockProvider>
            </div>
        </div>
    );
};

export default IndividualDashboardPreview;
